'use client';

import { FC, ReactNode, useState } from 'react';
import { shallow } from 'zustand/shallow';

import { ButtonIcon } from 'src/general/components/Button/ButtonIcon';
import { ScrollBox } from 'src/general/components/ScrollBox/ScrollBox';
import { Fade } from 'src/general/transitions/Fade';
import { Slide } from 'src/general/transitions/Slide';
import { getFilterParamFromCurrentFilters } from 'src/plp/helpers/getPlpUrlWithFilter';
import { plpStore } from 'src/stores/plpStore';
import { debounce } from 'src/utils/debounce';

import { PlpFilters } from '../PlpFilters';
import { FilterHierarchyFieldsGroupOption, FilterOption } from '../PlpFilters.type';
import { FilterOptionFieldsGroup } from '../components/Fields/FilterOptionFieldsGroup';
import { extractFilterOptions, updateCurrentFilterOptions } from '../plpFilterOptions.helper';
import { plpFiltersFormAction } from './PlpFiltersForm.action';
import styles from './PlpFiltersForm.module.scss';

type Props = {
  filters: ReturnType<typeof extractFilterOptions>;
  currentFiltersWithNoResults: FilterOption[];
  driveTrainHelper?: ReactNode;
};

export const PlpFiltersForm: FC<Props> = ({ filters, currentFiltersWithNoResults, driveTrainHelper }) => {
  const { currentFilters, filterParams, setCurrentFilters, setTotalResults, setResultUrl, setIsLoadingResults } =
    plpStore(
      (state) => ({
        totalResults: state.totalResults,
        currentFilters: state.currentFilters,
        setCurrentFilters: state.setCurrentFilters,
        setTotalResults: state.setTotalResults,
        setResultUrl: state.setResultUrl,
        setIsLoadingResults: state.setIsLoadingResults,
        filterParams: state.filterParams,
      }),
      shallow,
    );
  const [selectedHierarchy, setSelectedHierarchy] = useState<{
    option: FilterHierarchyFieldsGroupOption;
    filterTitle: string;
  } | null>(null);

  const onSelectFilter = async (selectedFilter: FilterOption) => {
    setIsLoadingResults(true);
    const updatedFilters = updateCurrentFilterOptions(currentFilters, selectedFilter);
    setCurrentFilters(updatedFilters);
    // Call the action to fetch total vehicles with result url
    const { totalResults, resultUrl } = await plpFiltersFormAction(updatedFilters, filterParams);
    setTotalResults(totalResults);
    setResultUrl(resultUrl);
    setIsLoadingResults(false);
  };

  const onSelectFilterDebounced = debounce(onSelectFilter, 100);
  const filterParam = getFilterParamFromCurrentFilters(currentFilters);
  return (
    <div className={styles.modalContainer}>
      <ScrollBox className={styles.root} data-testid="PlpFilters-root">
        <Fade isVisible={!selectedHierarchy} className={[styles.modalContent, styles.modalContentMain].join(' ')}>
          <PlpFilters
            filters={filters}
            filterParam={filterParam}
            currentFilters={currentFilters}
            currentFiltersWithNoResults={currentFiltersWithNoResults}
            driveTrainHelper={driveTrainHelper}
            type="modal"
            onSelectFilter={onSelectFilterDebounced}
            onSelectHierarchy={(filter) => setSelectedHierarchy(filter)}
          />
        </Fade>
        {/* Hierarchical sub-category */}
        <Slide isVisible={!!selectedHierarchy} className={styles.modalContent}>
          <header className={styles.modalHeader}>
            <div className={styles.modalHeaderClose}>
              <ButtonIcon
                title="Back"
                iconName="arrow-left"
                onClick={() => setSelectedHierarchy(null)}
                variant="tertiary"
                size="small"
                styling="round"
              />
            </div>
            {!!selectedHierarchy && <h2 className="c-fs-h5 font-extrabold">{selectedHierarchy.filterTitle}</h2>}
          </header>
          <ScrollBox className={styles.modalContentModel}>
            {!!selectedHierarchy && (
              <div className={styles.root} data-testid={`modal-hierarchy-${selectedHierarchy.filterTitle}`}>
                <FilterOptionFieldsGroup
                  options={selectedHierarchy.option.children!}
                  currentFilters={currentFilters}
                  onSelectFilter={onSelectFilterDebounced}
                  type="modal"
                />
              </div>
            )}
          </ScrollBox>
        </Slide>
      </ScrollBox>
    </div>
  );
};
